/*
  Theme resolution.

  Rules, in priority order:

  1. An explicit choice the technician made, from localStorage. It wins, always. A console
     used on a night shift under an OS set to light is a real situation.
  2. Otherwise the OS preference via prefers-color-scheme, consulted once at load.
  3. Otherwise dark, because this is an operations tool that sits open all day next to a
     remote desktop window.

  The OS preference is NOT tracked live once an explicit choice exists, because reacting to
  an OS change would silently overrule the technician.

  Only the theme name is persisted. It is a display preference, not user data, so
  localStorage is the right home for it. Nothing security relevant is stored by this
  console: the access token is memory only, see src/lib/api/token-store.ts.

  The same logic is duplicated as a tiny inline script in index.html so that first paint is
  already correct. Keep STORAGE_KEY in sync with that script.
*/

export const THEMES = ['dark', 'light'] as const;
export type Theme = (typeof THEMES)[number];

export const STORAGE_KEY = 'jit.console.theme';

function isTheme(value: string | null): value is Theme {
  return value === 'dark' || value === 'light';
}

export function readStoredTheme(): Theme | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return isTheme(raw) ? raw : null;
  } catch {
    // Storage can throw in a private window or with site data blocked. A theme is not
    // worth failing a render over.
    return null;
  }
}

export function systemTheme(): Theme {
  return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
}

export function resolveInitialTheme(): Theme {
  return readStoredTheme() ?? systemTheme();
}

export function persistTheme(theme: Theme): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    // Ignore: the theme still applies for this tab.
  }
}

export function applyTheme(theme: Theme): void {
  const root = document.documentElement;
  root.setAttribute('data-theme', theme);
  // Keeps native form controls, scrollbars and the browser chrome in step with the tokens.
  root.style.colorScheme = theme;
}
